/*
 * The Synex logo as data: the wordmark and the icon from the brand's exported
 * SVGs (primary, gray-mono, white-mono, white-mono-gray, icon), and their
 * colourways. Plain JavaScript, so the React logos (ApplicationLogo,
 * ApplicationIcon) and the cover template, which tools/covers also renders in
 * Node, draw the same shapes.
 *
 * Both are the X of two bars: `bar` runs top-left to bottom-right in the
 * colourway's ink, `sweep` crosses over it with the colourway's gradient,
 * from `from` at its lower-left end to `to` at its upper-right end.
 */

export const WORDMARK = Object.freeze({
    width: 432,
    height: 113,
    // The foot of the n's stems: where the letters stand.
    baseline: 80.4372,
    // "s", "y", "n", "e"; the y is drawn even-odd, as exported.
    letters: Object.freeze([
        { d: 'M35.2688 50.3739C41.1494 51.7185 44.0033 53.7313 43.8359 57.7625C43.8359 62.2985 39.4714 65.4886 33.5908 65.6546C26.0348 66.1595 19.6506 61.29 19.1457 55.7469H0C0.503551 72.5437 16.1231 82.1152 31.5752 82.1152C47.8684 82.1152 63.6526 74.0557 63.6526 57.9326C63.6526 51.5484 61.3036 39.4577 43.165 34.4182L30.0632 31.2281C25.5312 30.0536 21.8322 27.8666 21.8322 24.6765C21.8322 18.7999 26.5383 16.9504 31.7453 16.9504C36.4474 16.9504 41.8204 19.1361 42.3239 25.014H61.8072C60.9661 7.04136 46.189 0.32373 31.2418 0.32373C15.7896 0.32373 2.35305 10.9064 2.35305 23.668C2.35305 32.7387 4.19851 42.3103 24.5228 47.6874L35.2688 50.3739Z' },
        { d: 'M86.6272 112.052L101.889 74.3986L71.3223 2.01123H93.1545L112.639 50.8869L132.288 2.01123H154.122L108.365 112.052H86.6272Z', evenOdd: true },
        { d: 'M189.337 38.6167C189.337 25.014 197.063 20.3119 206.468 20.3119C215.368 20.3119 221.416 26.3546 221.416 37.7797V80.4372H241.908V34.0806C241.908 11.7448 230.99 0.32373 212.682 0.32373C203.444 0.32373 193.873 5.0258 189.337 13.5929V2.00179H168.846V80.4372H189.337V38.6167Z' },
        { d: 'M335.852 40.646C335.852 15.7897 318.217 0 296.047 0C273.71 0 255.401 15.7897 255.401 40.646C255.401 65.3309 273.71 81.7915 296.047 81.7915C310.491 81.7915 325.439 76.2524 332.661 62.8158C327.455 60.1252 321.912 57.1053 317.038 54.4147C313.177 61.1337 305.285 64.4939 297.388 64.4939C285.801 64.4939 277.068 58.2798 275.722 47.6971H335.515C335.681 45.8516 335.852 42.6616 335.852 40.646ZM275.556 33.7569C277.068 23.0109 285.465 17.1302 296.213 17.1302C306.292 17.1302 314.352 23.0109 315.864 33.7569H275.556Z' },
    ]),
    bar: 'M431.961 63.9798L368.003 0.0214844L347.479 20.5451L411.437 84.5033L431.961 63.9798Z',
    sweep: 'M411.475 0.0192593L347.517 63.9775L368.04 84.5011L431.998 20.5428L411.475 0.0192593Z',
    gradient: Object.freeze({ x1: 367.522, y1: 83.9833, x2: 431.996, y2: 20.5405 }),
    // The X's own square inside the wordmark.
    mark: Object.freeze({ x: 347.479, y: 0, size: 84.52 }),
});

export const ICON = Object.freeze({
    size: 63,
    bar: 'M62.5789 47.3777L15.2026 0.00146484L8.26006e-05 15.204L47.3763 62.5802L62.5789 47.3777Z',
    sweep: 'M47.4041 2.43688e-06L0.027832 47.3762L15.2304 62.5788L62.6066 15.2026L47.4041 2.43688e-06Z',
    gradient: Object.freeze({ x1: 14.8468, y1: 62.1952, x2: 62.6045, y2: 15.2007 }),
});

/**
 * The exported colourways: `primary` on light grounds, `white` (white-mono)
 * on dark ones; `grayMono` and `whiteGray` are the brand's monochrome pair.
 */
export const COLORWAYS = Object.freeze({
    primary: Object.freeze({ ink: '#474747', from: '#474747', to: '#F2641F' }),
    white: Object.freeze({ ink: '#FFFFFF', from: '#FFFFFF', to: '#F8AC86' }),
    grayMono: Object.freeze({ ink: '#474747', from: '#474747', to: '#B3B3B3' }),
    whiteGray: Object.freeze({ ink: '#FFFFFF', from: '#FFFFFF', to: '#999999' }),
});

/** A React id (useId's ":r1:") as one an SVG url(#...) reference can name. */
export const svgId = (prefix, reactId) => `${prefix}-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
